import { memo, useCallback } from 'react';
import Input from '@/components/Input.jsx';
import Select from '@/components/Select.jsx';
import RightPaneFieldV2 from '@/components/v2/RightPaneFieldV2.jsx';
import getValue from '@/lib/getValue.js';
import FilterItem from '../FilterItem.jsx';
import FilterContainerButtons from '../FilterContainerButtons.jsx';
import { randomId } from '@shared/utils/randomId.js';
import { LuTextCursorInput } from '@/components/react-icons/main';

const FilterContent = memo(({ dataSource, isTransformAction, isIfElse, lookupSettings, filter, fieldOptions, operatorOptions, isAdvanced = false, customOperators = false, useDynamicFields = false, canUseManualFieldEntry = false, onUseManualFieldEntry, onConjunctionChange, onFilterChange, onAdd, onAddGroup, onDelete, onToggleDisabled, loading, reload }) => {
  const isNested = filter?.type === 'nested';
  const isSingleField = ['exist', 'nexist', 'empty', 'nempty'].includes(filter?.operator);

  // Memoize change handlers for this specific filter
  const handleFieldChange = useCallback(
    ({ value }) => {
      onFilterChange(filter?.id, 'field', value);
    },
    [onFilterChange, filter?.id]
  );

  const handleOperatorChange = useCallback(
    ({ value }) => {
      onFilterChange(filter?.id, 'operator', value);
    },
    [onFilterChange, filter?.id]
  );

  const handleValueChange = useCallback(
    (value) => {
      onFilterChange(filter?.id, 'value', value);
    },
    [onFilterChange, filter?.id]
  );

  // Calculate default operator for new filters
  const getDefaultOperator = useCallback(() => {
    if (operatorOptions && operatorOptions.length > 0) {
      return operatorOptions[0].value;
    }
    return 'eq';
  }, [operatorOptions]);

  // Handlers for nested group add operations - add INSIDE the group
  const handleNestedAdd = useCallback(() => {
    const newFilter = { id: randomId(), field: '', operator: getDefaultOperator(), value: '' };
    const updatedFilter = {
      ...filter,
      filterSet: [...(filter.filterSet || []), newFilter]
    };

    // Update the nested group by modifying its filterSet directly
    onFilterChange(filter?.id, 'filterSet', updatedFilter.filterSet);
  }, [filter, onFilterChange, getDefaultOperator]);

  const handleNestedAddGroup = useCallback(() => {
    const newGroup = {
      id: randomId(),
      type: 'nested',
      conjunction: 'and',
      filterSet: [{ id: randomId(), field: '', operator: getDefaultOperator(), value: '' }]
    };
    const updatedFilter = {
      ...filter,
      filterSet: [...(filter.filterSet || []), newGroup]
    };

    // Update the nested group by modifying its filterSet directly
    onFilterChange(filter?.id, 'filterSet', updatedFilter.filterSet);
  }, [filter, onFilterChange, getDefaultOperator]);

  if (isNested) {
    return (
      <div className="filter-group">
        <FilterItem dataSource={dataSource} isTransformAction={isTransformAction} isIfElse={isIfElse} lookupSettings={lookupSettings} item={filter} fieldOptions={fieldOptions} operatorOptions={operatorOptions} isAdvanced={isAdvanced} customOperators={customOperators} useDynamicFields={useDynamicFields} canUseManualFieldEntry={canUseManualFieldEntry} onUseManualFieldEntry={onUseManualFieldEntry} onConjunctionChange={onConjunctionChange} onFilterChange={onFilterChange} onAdd={onAdd} onAddGroup={onAddGroup} onDelete={onDelete} onToggleDisabled={onToggleDisabled} />
        <FilterContainerButtons //
          isAdvanced={isAdvanced}
          onAdd={handleNestedAdd}
          onAddGroup={handleNestedAddGroup}
        />
      </div>
    );
  }

  return (
    <>
      {isIfElse && (
        <RightPaneFieldV2 //
          className="value-input"
          placeholder={'Field'}
          value={filter?.field}
          type={'lexical'}
          onChange={(value) => handleFieldChange({ value })}
          useDataTypes={true}
          dataType={filter?.field?.dataType}
          showLexicalActions={!!filter?.field?.dataType}
          dataArgumentKey="field"
        />
      )}
      {!isIfElse && useDynamicFields && <Input size="sm" className="field-select" placeholder="Enter field name" value={filter?.field || ''} onChange={(e) => handleFieldChange({ value: e?.target?.value })} />}
      {!isIfElse && !useDynamicFields && (
        <Select //
          compact
          size="sm"
          className="field-select"
          placeholder="Select..."
          options={fieldOptions}
          value={getValue(fieldOptions, filter?.field)}
          onChange={handleFieldChange}
          floatingSelectV2Props={fieldOptions?.some((n) => n?.tag) ? { width: 400, offset: 8, smartWidth: true } : { smartWidth: true }}
          dataArgumentKey="field"
          loading={loading}
          reload={reload}
          actionIcon={canUseManualFieldEntry ? LuTextCursorInput : undefined}
          actionTitle="Enter Field Name"
          onAction={canUseManualFieldEntry ? onUseManualFieldEntry : undefined}
        />
      )}
      <Select //
        compact
        size="sm"
        placeholder="Select..."
        className="operator-select"
        options={operatorOptions}
        value={getValue(operatorOptions, filter?.operator)}
        onChange={handleOperatorChange}
        operator={!customOperators}
        floatingSelectV2Props={!customOperators ? { width: 400, center: true, placement: 'bottom-center', offset: 8 } : {}}
        dataArgumentKey="operator"
      />
      {!isSingleField && (
        <RightPaneFieldV2 //
          className="value-input"
          placeholder="Value"
          value={filter?.value}
          type="lexical"
          onChange={handleValueChange}
          useDataTypes={true}
          dataType={filter?.value?.dataType}
          showLexicalActions={!!filter?.value?.dataType}
          allowSelfOutput={true}
          dataArgumentKey="value"
        />
      )}
    </>
  );
});

FilterContent.displayName = 'FilterContent';

export default FilterContent;
